import axios from 'axios'
import store from '../store'
import Vue from 'vue'
import { AppVersion, OS, OSVersion } from '../deviceDetails'

const baseURL = `${process.env.VUE_APP_BASE_URL}api/d`;

const axiosInstance = axios.create({
	baseURL,
	headers: {
		OS: 'Windows',
		OSVersion: '13.0',
		AppVersion,
		Platform: 'Web'
	},
	timeout: 30000 // 30 sec timeout
})

let refreshTokenPromise = null // Store the refresh promise to reuse

// Helper function to get deviceId consistently
function getDeviceId() {
	return localStorage.getItem('deviceId') || 
	       localStorage.getItem('ip') || 
	       store.getters['locationStore/getLocationIpAddress'] || 
	       'web_unknown';
}

// Helper function to get screen name
function getScreenName() {
	return localStorage.getItem('isSubUser') === 'true'
		? "Profile"
		: store.getters.getScreenName;
}

// REQUEST INTERCEPTOR
axiosInstance.interceptors.request.use(
	(config) => {
		const isLoginEndpoint = config.url.includes('/Login') || config.url.includes('/RefreshToken');
		
		if (!isLoginEndpoint) {
			config.headers['Authorization'] = store.getters.getToken;
			config.headers['UserId'] = store.getters.getUserId;
			config.headers['ScreenName'] = getScreenName();
			config.headers['x-api-version'] = '1.0';
			config.headers['DeviceId'] = getDeviceId();
		} else if (config.url.includes('/Login')) {
			config.headers['ScreenName'] = "Login";
			config.headers['DeviceId'] = getDeviceId();
		}
		return config;
	},
	(error) => {
		console.error('Request interceptor error:', error);
		return Promise.reject(error);
	}
)

// RESPONSE INTERCEPTOR
axiosInstance.interceptors.response.use(
	async (response) => {
		// ✅ FIX 1: Handle 401 in response data
		if (response.data?.statusCode === 401) {
			const originalRequest = response.config;
			
			// Prevent infinite loops
			if (originalRequest._retry) {
				return response;
			}
			originalRequest._retry = true;

			try {
				// ✅ FIX 2: Use promise to handle concurrent requests
				if (!refreshTokenPromise) {
					refreshTokenPromise = performTokenRefresh();
				}
				
				const newToken = await refreshTokenPromise;
				
				// ✅ FIX 3: Retry original request with new token
				originalRequest.headers['Authorization'] = newToken;
				return axiosInstance(originalRequest);
				
			} catch (error) {
				// Refresh failed, logout user
				handleLogout();
				return Promise.reject(error);
			} finally {
				refreshTokenPromise = null;
			}
		}
		
		return response;
	},
	async (error) => {
		// ✅ FIX 4: Handle network errors gracefully
		if (!error.response) {
			console.error('Network error - no response from server:', error.message);
			return Promise.reject({
				message: 'Network Error: Please check your internet connection',
				isNetworkError: true,
				originalError: error
			});
		}
		
		const originalRequest = error.config;
		
		// ✅ FIX 5: Handle 401 errors consistently
		if (error.response?.status === 401) {
			// Prevent infinite loops
			if (originalRequest._retry) {
				handleLogout();
				return Promise.reject(error);
			}
			originalRequest._retry = true;

			try {
				// Use promise to handle concurrent requests
				if (!refreshTokenPromise) {
					refreshTokenPromise = performTokenRefresh();
				}
				
				const newToken = await refreshTokenPromise;
				
				// Retry original request with new token
				originalRequest.headers['Authorization'] = newToken;
				return axiosInstance(originalRequest);
				
			} catch (refreshError) {
				// Refresh failed, logout user
				handleLogout();
				return Promise.reject(refreshError);
			} finally {
				refreshTokenPromise = null;
			}
		}
		
		// Return structured error for other cases
		return Promise.reject({
			message: error.response?.data?.message || error.message || 'An error occurred',
			status: error.response?.status,
			data: error.response?.data,
			originalError: error
		});
	}
)

// ✅ FIX 6: Centralized token refresh logic
async function performTokenRefresh() {
	try {
		const storedRefreshToken = localStorage.getItem('refreshToken');
		
		if (storedRefreshToken) {
			// Use refresh token endpoint
			const response = await axios.post(`${baseURL}/RefreshToken`, {}, {
				headers: {
					OS,
					OSVersion,
					AppVersion,
					Platform: 'Web',
					UserId: store.getters.getUserId,
					ScreenName: getScreenName(),
					DeviceId: getDeviceId(),
					Authorization: storedRefreshToken
				}
			});

			const newData = response.data?.data ?? response.data;
			const newToken = newData?.Token || newData?.token;
			const newRefreshToken = newData?.refreshToken || newData?.RefreshToken;

			if (!newToken) {
				throw new Error('No token received from refresh endpoint');
			}

			// Update store and localStorage
			store.commit('setToken', newToken);
			if (newRefreshToken) {
				store.commit('setRefreshToken', newRefreshToken);
				localStorage.setItem('refreshToken', newRefreshToken);
			}

			console.log('✅ Token refreshed successfully');
			return newToken;
			
		} else {
			// Fallback: Re-login using stored credentials
			const user = store.getters.getUser;
			
			if (!user || !user.userName || !user.password) {
				throw new Error('No stored credentials available');
			}

			const response = await axios.post(
				`${baseURL}/Login`,
				{
					username: Vue.CryptoJS.AES.decrypt(user.userName, Vue.prototype.$secretKey).toString(Vue.CryptoJS.enc.Utf8),
					password: Vue.CryptoJS.AES.decrypt(user.password, Vue.prototype.$secretKey).toString(Vue.CryptoJS.enc.Utf8),
					city: store.getters['locationStore/getLocationCity'] ?? 'unknown',
					state: store.getters['locationStore/getLocationState'] ?? 'unknown',
					country: store.getters['locationStore/getLocationCountry'] ?? 'unknown',
				},
				{
					headers: {
						OS,
						OSVersion,
						AppVersion,
						Platform: 'Web',
						ScreenName: getScreenName(),
						DeviceId: getDeviceId()
					}
				}
			);

			const newData = response.data?.data ?? response.data;
			const newToken = newData?.token || newData?.Token;
			const newRefreshToken = newData?.refreshToken || newData?.RefreshToken;

			if (!newToken) {
				throw new Error('No token received from login');
			}

			store.commit('setToken', newToken);
			if (newRefreshToken) {
				store.commit('setRefreshToken', newRefreshToken);
				localStorage.setItem('refreshToken', newRefreshToken);
			}

			console.log('✅ Re-logged in successfully');
			return newToken;
		}
		
	} catch (error) {
		console.error('❌ Token refresh failed:', error);
		throw error;
	}
}

// ✅ FIX 7: Centralized logout logic
function handleLogout() {
	console.log('🚪 Session expired - logging out');
	store.commit('setUserBlank');
	store.commit('setPrescriptionList', []);
	store.commit('setProfileImage', null);
	store.commit('CLOSE_DRAWER');
	localStorage.removeItem('isLogin');
	localStorage.removeItem('refreshToken');
	
	// Redirect to login page
	if (window.location.pathname !== '/') {
		window.location.href = '/';
	}
}

export default () => {
	return axiosInstance
}

// Export the instance directly for use in other modules
export { axiosInstance }